// MaturaCubicRootsSumClosedTask.js
// Zadanie maturalne zamknięte: suma wszystkich rozwiązań równania
// (a1 x + b1)(a2 x + b2)(x + b3) = 0
// Jeden czynnik ma współczynnik 1 przy x, pozostałe mają |a| > 1 i są różne.

// ---------------------------------------------------------------------------
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ---------------------------------------------------------------------------
// GENERATOR
// ---------------------------------------------------------------------------

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 10:
 *   (3x − 12)(10 + 5x)(x − 3) = 0  →  x ∈ {4, −2, 3},  suma = 5  (C).
 */
function createOriginalTask() {
  const options = [
    { label: "A", value: -5, isCorrect: false },
    { label: "B", value: -1, isCorrect: false },
    { label: "C", value: 5, isCorrect: true },
    { label: "D", value: 9, isCorrect: false },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaCubicRootsSumClosedTask",
    data: {
      r1: 4,
      r2: -2,
      r3: 3,
      a1: 3,
      a2: 5,
      b1: -12,
      b2: 10,
      b3: -3,
      exprLatex: "(3x - 12)(10 + 5x)(x - 3)",
      sumRoots: 5,
      sumAbsRoots: 9,
      sumConstants: -5,
      options,
      correctIndex,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 10,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  // losujemy trzy całkowite, różne pierwiastki (bez zera)
  const possibleRoots = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5];
  const r1 = pick(possibleRoots);
  let r2;
  do {
    r2 = pick(possibleRoots);
  } while (r2 === r1);
  let r3;
  do {
    r3 = pick(possibleRoots);
  } while (r3 === r1 || r3 === r2);

  // współczynniki przy pierwszych dwóch czynnikach: 2–5, różne
  const coeffPool = [2, 3, 4, 5];
  const a1 = pick(coeffPool);
  let a2;
  do {
    a2 = pick(coeffPool);
  } while (a2 === a1);

  // czynniki w postaci (a1 x - a1 r1)(a2 x - a2 r2)(x - r3)
  const b1 = -a1 * r1;
  const b2 = -a2 * r2;
  const b3 = -r3;

  const factor1Latex =
    b1 >= 0 ? `${a1}x + ${b1}` : `${a1}x - ${Math.abs(b1)}`;
  const factor2Latex =
    b2 >= 0 ? `${a2}x + ${b2}` : `${a2}x - ${Math.abs(b2)}`;
  const factor3Latex =
    b3 >= 0 ? `x + ${b3}` : `x - ${Math.abs(b3)}`;

  // ⬇⬇ NOWE: losujemy kolejność trzech nawiasów,
  // żeby „samotny” x nie był zawsze na końcu
  const factorsShuffled = shuffle([factor1Latex, factor2Latex, factor3Latex]);
  const exprLatex = factorsShuffled.map((f) => `(${f})`).join("");

  const sumRoots = r1 + r2 + r3;
  const sumAbsRoots = Math.abs(r1) + Math.abs(r2) + Math.abs(r3);
  const sumConstants = b1 + b2 + b3; // analog do -12+10-3 = -5 w CKE
  const negSumRoots = -sumRoots;

  let rawOptions = [
    { value: sumRoots, isCorrect: true },
    { value: negSumRoots, isCorrect: false },
    { value: sumAbsRoots, isCorrect: false }, // suma wartości bezwzględnych
    { value: sumConstants, isCorrect: false },
  ];

  // zabezpieczenie przed zduplikowaniem odpowiedzi
  const seen = new Set();
  rawOptions = rawOptions.map((opt) => {
    let v = opt.value;
    while (seen.has(v)) v += 2;
    seen.add(v);
    return { ...opt, value: v };
  });

  const shuffledOptions = shuffle(rawOptions).map((o, idx) => ({
    label: LETTERS[idx],
    value: o.value,
    isCorrect: o.isCorrect,
  }));

  const correctIndex = shuffledOptions.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaCubicRootsSumClosedTask",
    data: {
      r1,
      r2,
      r3,
      a1,
      a2,
      b1,
      b2,
      b3,
      exprLatex,
      sumRoots,
      sumAbsRoots,
      sumConstants,
      options: shuffledOptions,
      correctIndex,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_10" },
  };
}

// ---------------------------------------------------------------------------
// RENDER TREŚCI
// ---------------------------------------------------------------------------

export function renderTask({ data }) {
  const { exprLatex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Suma wszystkich rozwiązań równania{" "}
        <InlineMath math={`${exprLatex} = 0`} /> jest równa:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// INPUT (ABCD)
// ---------------------------------------------------------------------------

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      value: opt.value,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ---------------------------------------------------------------------------
// SPRAWDZANIE / ODPOWIEDZI
// ---------------------------------------------------------------------------

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.value}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, value } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${value}`} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// WYJAŚNIENIE
// ---------------------------------------------------------------------------

export function renderExplanation({ data }) {
  const { exprLatex, r1, r2, r3, sumRoots } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Zauważamy, że równanie ma postać iloczynu
        trzech czynników liniowych:
      </p>

      <BlockMath math={`${exprLatex} = 0`} />

      <p>
        Iloczyn jest równy zeru wtedy i tylko wtedy, gdy przynajmniej jeden
        z czynników jest równy zero.
      </p>

      <p>
        <strong>Krok 2.</strong> Rozwiązujemy równania wynikające z
        poszczególnych czynników:
      </p>

      <ul style={{ paddingLeft: "1.5rem" }}>
        <li>
          <BlockMath math={`${exprLatex.split(")")[0]}) = 0`} />
          <BlockMath math={`x = ${r1}`} />
        </li>
        <li>
          <BlockMath math={`(${exprLatex.split(")")[1].slice(1)}) = 0`} />
          <BlockMath math={`x = ${r2}`} />
        </li>
        <li>
          <BlockMath math={`(${exprLatex.split(")")[2].slice(1)}) = 0`} />
          <BlockMath math={`x = ${r3}`} />
        </li>
      </ul>

      <p>
        Zatem rozwiązaniami równania są liczby{" "}
        <InlineMath math={`x_1 = ${r1},\\; x_2 = ${r2},\\; x_3 = ${r3}`} />.
      </p>

      <p>
        <strong>Krok 3.</strong> Obliczamy sumę wszystkich rozwiązań:
      </p>

      <div style={{ textAlign: "center" }}>
        <BlockMath
          math={`x_1 + x_2 + x_3 = ${r1} + ${r2} + ${r3} = ${sumRoots}`}
        />
      </div>

      <p>
        <strong>Wniosek.</strong> Suma wszystkich rozwiązań równania jest
        równa <InlineMath math={`${sumRoots}`} />.
      </p>
    </div>
  );
}

